// `window` is provided by ESLint's globals.browser preset (eslint.config.js).
/*
 * src/data/caseStudy.js — structured content for the first public case study.
 *
 * Pairs with `src/data/heroMetrics.js` (the three numbers in the Hero tiles
 * MUST match the corresponding entries in `metrics` here). Localized copy
 * (intro paragraphs, timeline event labels, quote text) lives in
 * `src/i18n.jsx` under the `case.study.*` keys; THIS file holds only the
 * locale-agnostic structure: slug, clinic identity (anonymized until
 * PR-DEP-1), numeric metrics, ISO timeline anchors, publish date.
 *
 * Wiring: loaded BEFORE src/Hero.jsx, src/Case.jsx, src/CaseStudy.jsx by
 * `index.html` and the per-locale shells emitted by
 * `scripts/build-locale-html.js`. The build script reads `slug` from this
 * file as the single source of truth for the case-study URL.
 *
 * Feature flag: when `window.__HB_CASE_STUDY_ENABLED__` is `false`, the
 * Hero secondary CTA keeps its legacy `#case` anchor instead of pointing
 * to `/case/{slug}/`. See Phase H.1 in the plan — the engineering merges
 * with the flag DEFAULT FALSE so the route ships dark until PR-DEP-1..7
 * are signed off.
 *
 * Anonymization rules (from the plan and `docs/case-studies.md`):
 *   - No patient names, initials, photos, DOBs, exact ages.
 *   - Age ranges only ("30–45"), no rare conditions in small geographies.
 *   - Patient quotes paraphrased and clinic-reviewed.
 *   - `clinic.name` and `clinic.logo` stay `null` until PR-DEP-1 lands;
 *     i18n key `case.study.clinic.codename` is used by the renderer in the
 *     interim ("a private mammology clinic in Kyiv").
 *   - `quote.author` and `quote.role` stay `null` until PR-DEP-1 + PR-DEP-3.
 */
window.HEALBOT_CASE_STUDY = {
  // Slug locks early — baked into URL, sitemap, OG og:url, analytics labels.
  // Lower-case ASCII only (a-z, 0-9, '-'). See PR-DEP-7 in the plan.
  slug: "kyiv-mammology-pilot",

  // Clinic identity. `name` + `logo` flip non-null only after PR-DEP-1.
  clinic: {
    name: null, // e.g. "Clinic Foo" — populated post PR-DEP-1.
    logo: null, // e.g. "/og/clinic-foo-logo.svg" — populated post PR-DEP-1.
    region: "Kyiv, Ukraine", // Generic regional descriptor; safe pre-permission.
    specialty: "mammology", // English; locale labels come from i18n.
    anonymized: true, // Flips to `false` once PR-DEP-1 is signed.
  },

  // Mirror of the three Hero metrics with deeper context. Keep the keys in
  // sync with src/data/heroMetrics.js so a stale-data drift is impossible.
  metrics: [
    {
      key: "bookingsUplift",
      // `value`/`unit` MUST match window.HEALBOT_HERO_METRICS.bookingsUplift.
      value: null,
      unit: "%",
      labelKey: "case.study.metric.bookings.label",
      detailKey: "case.study.metric.bookings.detail",
    },
    {
      key: "avgReplyTime",
      value: null,
      unit: "min",
      labelKey: "case.study.metric.reply.label",
      detailKey: "case.study.metric.reply.detail",
    },
    {
      key: "frontDeskHours",
      value: null,
      unit: "hrs/week",
      labelKey: "case.study.metric.frontdesk.label",
      detailKey: "case.study.metric.frontdesk.detail",
    },
  ],

  // ISO `date`s anchor the timeline; localized event copy lives in i18n keys
  // `case.study.timeline.eventN`. Order is chronological, oldest first.
  // Until PR-DEP-2 lands the dates are placeholder-`null`; renderer hides
  // the timeline section when the array is empty.
  timeline: [],

  // Headline patient/operator quote. All fields are `null` until PR-DEP-1
  // and PR-DEP-3 land; renderer hides the quote section when text is null.
  quote: {
    textKey: null, // e.g. "case.study.quote.text" — set when copy approved.
    author: null, // e.g. "Operations lead, partner clinic" — anonymized.
    role: null, // e.g. "Mammologist, 12 years experience".
  },

  // ISO date the case study was published. `null` until PR-DEP-1..5 all green
  // AND the rollout flag flips. Used by `<meta property="article:published_time">`
  // and the "Last updated" footnote on the case-study page.
  publishedAt: null,

  // Open Graph share image, served from `/og/case-{slug}.png` (1200×630 PNG).
  // See PR-DEP-6. Falls back to `/og-image.png` when `null`.
  ogImage: null,
};

/*
 * Feature flag: gates the Hero CTA href + the build-script case-study shell
 * emission. Default FALSE so the engineering can merge to main without
 * shipping an empty case-study page to crawlers. Operator flips this to
 * `true` in the build environment (or via a `<script>` injected by the
 * shell) once PR-DEP-1..7 are signed off.
 *
 * See Phase H.1 in the plan and `docs/case-studies.md` §"Rollout".
 */
if (typeof window.__HB_CASE_STUDY_ENABLED__ === "undefined") {
  window.__HB_CASE_STUDY_ENABLED__ = false;
}
